import type Phaser from 'phaser';

/**
 * The drawing kit of the spells' effects (`spell-effects.ts`,
 * `spell-effects-grimoire.ts`): palette colours, the painter's depth of a
 * pair of feet, and a counter from 0 to 1 that redraws a graphics each frame
 * in whole 1×1 pixels at rounded positions (CLAUDE.md rule 6).
 */

/** Palette colours (data/assets/palette.gpl). */
export const WHITE = 0xf8f2ea;
export const GOLD_TIP = 0xfff1a8;
export const GOLD = 0xffc82e;
export const ORANGE = 0xf07a1c;
export const EMBER = 0xc23a12;
export const EMBER_DARK = 0x532622;
export const BLOOD = 0xbd3644;
export const METAL_DARK = 0x6b6b7c;
export const METAL = 0xa9a9b8;
export const METAL_LIGHT = 0xd6d6e0;
export const EARTH_LIGHT = 0xa35c37;
export const EARTH = 0x81442e;
export const EARTH_DARK = 0x433026;
export const SOOT = 0x2a1c1a;

/** Feet of a character on screen, as the scene draws it: bottom centre of its tile. */
export interface Feet {
  x: number;
  y: number;
}

/** Painter's order of the scene: a character's depth is its feet's y. */
export function over(feet: Feet): number {
  return feet.y + 1;
}

export function behind(feet: Feet): number {
  return feet.y - 1;
}

/** Run `draw(progress)` on a fresh graphics every frame for `durationMs`, then clear it away. */
export function animate(
  scene: Phaser.Scene,
  depth: number,
  durationMs: number,
  draw: (graphics: Phaser.GameObjects.Graphics, progress: number) => void,
  delayMs = 0,
): void {
  const graphics = scene.add.graphics().setDepth(depth);
  scene.tweens.addCounter({
    from: 0,
    to: 1,
    duration: durationMs,
    delay: delayMs,
    onUpdate: (tween) => {
      graphics.clear();
      draw(graphics, tween.getValue() ?? 0);
    },
    onComplete: () => {
      graphics.destroy();
    },
  });
}

export function dot(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  color: number,
): void {
  graphics.fillStyle(color, 1);
  graphics.fillRect(Math.round(x), Math.round(y), 1, 1);
}

/** Fully opaque for the first part, then a linear fade to nothing. */
export function fadeAfter(progress: number, start: number): number {
  return progress < start ? 1 : Math.max(0, 1 - (progress - start) / (1 - start));
}

/**
 * A flattened ring on the ground, the half behind the body drawn under it and
 * the half in front over it: `draw(front)` is asked for each half.
 */
export function groundRing(
  scene: Phaser.Scene,
  feet: Feet,
  durationMs: number,
  draw: (front: boolean) => (graphics: Phaser.GameObjects.Graphics, progress: number) => void,
  delayMs = 0,
): void {
  animate(scene, behind(feet), durationMs, draw(false), delayMs);
  animate(scene, over(feet), durationMs, draw(true), delayMs);
}

/** The dots of an ellipse's half, front (lower) or back (upper). */
export function ellipseDots(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  front: boolean,
  color: (step: number) => number,
): void {
  const steps = Math.max(16, Math.round(rx * 5));
  for (let step = 0; step < steps; step += 1) {
    const angle = (step / steps) * Math.PI * 2;
    const sin = Math.sin(angle);
    if (sin >= 0 !== front) continue;
    dot(graphics, cx + Math.cos(angle) * rx, cy + sin * ry, color(step));
  }
}
